import { useEffect, useState } from "react";

/** Tracks which section is currently in view so the menu can highlight it.
 *  Uses scroll position against a line just below the fixed header, which
 *  behaves predictably for both tall and short sections. */
export function useActiveSection(ids: string[], enabled = true) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setActive(null);
      return;
    }

    let frame = 0;

    const compute = () => {
      frame = 0;
      const headerRaw = getComputedStyle(document.documentElement).getPropertyValue("--site-hdr");
      const header = Number.parseFloat(headerRaw) || 72;
      const line = header + 32; // a little below the header, with tolerance

      let current: string | null = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= line && rect.bottom > line) {
          current = id;
          break;
        }
        if (rect.top <= line) current = id;
      }

      // At the very bottom of the page, favour the last visible section.
      const atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        for (let i = ids.length - 1; i >= 0; i -= 1) {
          if (document.getElementById(ids[i])) {
            current = ids[i];
            break;
          }
        }
      }

      setActive(current);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ids.join("|"), enabled]);

  return active;
}
